import { useEffect, useState } from "react";
import { BookMarked, Trash2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { useToast } from "@/lib/toast";

// Internal "price memory" — past buy costs captured from saved quotations and
// AI spec-sheet research. Never shown to customers.
export const PriceMemoryCard = () => {
  const [entries, setEntries] = useState([]);
  const [q, setQ] = useState("");
  const { toast } = useToast();

  const load = (query = "") =>
    api.priceBook(query, 100).then(setEntries).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (e) => {
    if (!window.confirm(`Forget "${e.description}"?`)) return;
    try {
      await api.deletePriceBookEntry(e.id);
      toast("Removed from price memory", "success");
      load(q);
    } catch { toast("Delete failed", "error"); }
  };

  return (
    <div className="card card-padded" data-testid="price-memory-card">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-2 font-bold text-sm text-amber-800">
          <BookMarked size={15} /> Price Memory
          <span className="text-[10px] font-normal uppercase tracking-wider bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">
            internal — buy costs
          </span>
        </div>
        <input
          data-testid="price-memory-search"
          className="input !w-56 ml-auto"
          placeholder="Search remembered items…"
          value={q}
          onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
        />
      </div>
      <p className="muted text-xs mb-3">
        Remembered automatically from saved quotations & AI market research. Type an item
        description in any quotation and pick a match to price it instantly.
      </p>
      {entries.length === 0 ? (
        <div className="muted text-sm py-6 text-center">
          Nothing remembered yet — save a quotation with internal costs to start building your price memory.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs muted border-b border-slate-100">
                <th className="px-2 py-2">Item</th>
                <th className="px-2 py-2">Unit</th>
                <th className="px-2 py-2 text-right text-amber-700">Buy cost</th>
                <th className="px-2 py-2 text-right">Mgn %</th>
                <th className="px-2 py-2 text-right">Sale price</th>
                <th className="px-2 py-2">Cheapest source</th>
                <th className="px-2 py-2 text-right">Used</th>
                <th className="px-2 py-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const sup = (e.suppliers || [])[0];
                return (
                  <tr key={e.id} className="border-b border-slate-50" data-testid={`pm-row-${e.id}`}>
                    <td className="px-2 py-1.5 max-w-xs truncate" title={e.description}>{e.description}</td>
                    <td className="px-2 py-1.5 muted">{e.unit_of_measure}</td>
                    <td className="px-2 py-1.5 text-right text-amber-700 font-medium">
                      {formatCurrency(e.cost_price, e.currency)}
                    </td>
                    <td className="px-2 py-1.5 text-right">{e.margin_pct || 0}%</td>
                    <td className="px-2 py-1.5 text-right font-medium">{formatCurrency(e.unit_price, e.currency)}</td>
                    <td className="px-2 py-1.5 text-xs">
                      {sup ? (
                        sup.url ? (
                          <a href={sup.url} target="_blank" rel="noreferrer"
                             className="text-[#0e6939] hover:underline inline-flex items-center gap-0.5">
                            {sup.name} <ExternalLink size={10} />
                          </a>
                        ) : sup.name
                      ) : <span className="muted">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right muted">{e.times_used || 1}×</td>
                    <td className="px-2 py-1.5">
                      <button data-testid={`pm-delete-${e.id}`} onClick={() => remove(e)}
                              className="text-slate-300 hover:text-rose-500 transition" title="Forget this price">
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
